import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, MapPin, MoreVertical, Upload, Image, X, Trash2 } from 'lucide-react';
import AWS from 'aws-sdk';
import { getSites, createSite, deleteSiteWithContainers, getContainers, updateContainer, createContainer, updateContainerPayment, deleteContainer } from './dynamodb';

// Configure AWS
AWS.config.update({
  region: process.env.REACT_APP_AWS_REGION,
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY
});

const s3 = new AWS.S3();

// Container status configurations
const statusConfig = {
  available: { color: 'bg-gray-400', label: 'Available', textColor: 'text-white' },
  'rented-paid': { color: 'bg-green-500', label: 'Rented', textColor: 'text-white' },
  'rented-unpaid': { color: 'bg-yellow-500', label: 'Rented - Unpaid', textColor: 'text-white' },
  abandoned: { color: 'bg-red-500', label: 'Abandoned', textColor: 'text-white' }
};

function SiteLocations({ onSiteSelect }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteAddress, setNewSiteAddress] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, site: null });
  const [deleteSiteConfirm, setDeleteSiteConfirm] = useState('');
  const [deleteSiteLoading, setDeleteSiteLoading] = useState(false);

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      setLoading(true);
      const sitesData = await getSites();
      setSites(sitesData || []);
    } catch (error) {
      console.error('Error loading sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSite = async () => {
    if (newSiteName.trim()) {
      try {
        const newSite = await createSite({
          name: newSiteName,
          address: newSiteAddress
        });
        setSites([...sites, newSite]);
        setNewSiteName('');
        setNewSiteAddress('');
        setShowAddModal(false);
      } catch (error) {
        console.error('Error creating site:', error);
        alert('Error creating site');
      }
    }
  };

  const openDeleteSite = (site) => {
    setDeleteModal({ open: true, site });
    setDeleteSiteConfirm('');
  };

  const closeDeleteSite = () => {
    setDeleteModal({ open: false, site: null });
    setDeleteSiteConfirm('');
    setDeleteSiteLoading(false);
  };

  const confirmDeleteSite = async () => {
    const site = deleteModal.site;
    if (!site) return;
    if (deleteSiteConfirm.trim() !== site.name) return;
    try {
      setDeleteSiteLoading(true);
      await deleteSiteWithContainers(site.id);
      await loadSites();
      closeDeleteSite();
    } catch (error) {
      console.error('Error deleting site:', error);
      alert('Error deleting site');
      setDeleteSiteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading sites...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Site Locations</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Plus size={20} />
          Add Site
        </button>
      </div>

      {sites.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">No sites found. Add your first site to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sites.map((site) => (
            <div key={site.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">{site.name}</h3>
                  {site.address && (
                    <p className="text-gray-600 text-sm">{site.address}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onSiteSelect(site)}
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg transition-colors"
                  >
                    Manage Containers
                  </button>
                  <button
                    onClick={() => openDeleteSite(site)}
                    className="p-2 rounded-md text-red-600 hover:bg-red-50"
                    aria-label="Delete site"
                    title="Delete site"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Site Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add New Site</h2>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Site Name"
                value={newSiteName}
                onChange={(e) => setNewSiteName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <input
                type="text"
                placeholder="Address"
                value={newSiteAddress}
                onChange={(e) => setNewSiteAddress(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleAddSite}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg"
              >
                Add Site
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteModal.open && deleteModal.site && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-3 text-red-700">Delete Site</h2>
            <p className="text-sm text-gray-700 mb-4">
              This will permanently delete the site <span className="font-semibold">{deleteModal.site.name}</span> and all containers within it from DynamoDB. This cannot be undone.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type the site name to confirm</label>
            <input
              type="text"
              value={deleteSiteConfirm}
              onChange={(e) => setDeleteSiteConfirm(e.target.value)}
              placeholder={deleteModal.site.name}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={confirmDeleteSite}
                disabled={deleteSiteConfirm.trim() !== deleteModal.site.name || deleteSiteLoading}
                className={`flex-1 ${deleteSiteConfirm.trim() === deleteModal.site.name && !deleteSiteLoading ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-300'} text-white py-2 rounded-lg`}
              >
                {deleteSiteLoading ? 'Deleting…' : 'Delete Site'}
              </button>
              <button
                onClick={closeDeleteSite}
                disabled={deleteSiteLoading}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ContainerGrid({ site, onBack }) {
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContainers, setSelectedContainers] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [addCustomerLoading, setAddCustomerLoading] = useState(false);
  const [addCustomerError, setAddCustomerError] = useState('');
  const [addCustomerForm, setAddCustomerForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    billingFrequency: 'weekly',
    nextBillingDate: ''
  });
  const [stripeReady, setStripeReady] = useState(false);
  const cardMountRef = React.useRef(null);
  const stripeRef = React.useRef(null);
  const elementsRef = React.useRef(null);
  const cardRef = React.useRef(null);
  const bankMountRef = React.useRef(null);
  const bankRef = React.useRef(null);
  const [paymentMethodType, setPaymentMethodType] = useState('card'); // 'card' | 'au_becs_debit'
  const [showAddModal, setShowAddModal] = useState(false);
  const [bulkNumbersText, setBulkNumbersText] = useState('');
  const [siteMapFile, setSiteMapFile] = useState(null);
  const [siteMapPreview, setSiteMapPreview] = useState(null);
  const [uploadingSiteMap, setUploadingSiteMap] = useState(false);
  const [siteMapUrl, setSiteMapUrl] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ open: false, type: null }); // type: 'available' | 'abandoned'
  const [deleteContainerModal, setDeleteContainerModal] = useState(false);
  const [deleteContainerChecked, setDeleteContainerChecked] = useState(false);
  const [deleteContainerLoading, setDeleteContainerLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadContainers();
  }, [site.id]);

  useEffect(() => {
    setShowBulkActions(selectedContainers.length > 0);
  }, [selectedContainers]);

  useEffect(() => {
    loadExistingSiteMap();
  }, [site.id]);

  const loadContainers = async () => {
    try {
      setLoading(true);
      const containersData = await getContainers(site.id);
      setContainers(containersData || []);
    } catch (error) {
      console.error('Error loading containers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExistingSiteMap = async () => {
    try {
      // Folder structure: site-maps/<site-name-slug>/site-map.<ext>
      const slug = site.name.toLowerCase().replace(/\s+/g, '-');
      const listParams = { Bucket: 'storehere-agreements', Prefix: `site-maps/${slug}/` };
      
      const result = await s3.listObjectsV2(listParams).promise();
      
      if (result.Contents && result.Contents.length > 0) {
        // There should only be one; pick the most recent if multiple
        const latestSiteMap = result.Contents.sort((a, b) => new Date(b.LastModified) - new Date(a.LastModified))[0];
        
        // Generate signed URL for viewing the image
        const signedUrl = await s3.getSignedUrlPromise('getObject', {
          Bucket: 'storehere-agreements',
          Key: latestSiteMap.Key,
          Expires: 3600 // 1 hour
        });
        
        setSiteMapUrl(signedUrl);
      }
    } catch (error) {
      console.error('Error loading existing site map:', error);
      // Don't show error to user - just means no existing site map or no permissions
    }
  };

  // Filter containers and determine status based on payment data
  const containerItems = containers.filter(item => item.type === 'container').map(container => {
    // Auto-determine status based on payment data, but never override 'abandoned'
    let status = container.status;
    if (status !== 'abandoned') {
      if (container.subscriptionStatus === 'active' && container.securityBondStatus === 'paid') {
        status = 'rented-paid';
      } else if (container.subscriptionStatus === 'past_due' || container.overdueSince) {
        status = 'rented-unpaid';
      } else if (container.subscriptionStatus === 'canceled' || container.subscriptionStatus === 'inactive') {
        status = 'available';
      }
    }
    return { ...container, status };
  });

  // Selected container helpers
  const selectedContainerObj = containerItems.find(c => c.number === selectedContainers[0]);
  const canAddCustomer = !!selectedContainerObj && selectedContainerObj.status === 'available';
  const canViewCustomer = !!selectedContainerObj && !!selectedContainerObj.customerEmail;
  
  // Sort containers and fill left side first
  const sortedContainers = containerItems.sort((a, b) => {
    // Handle numeric sorting for numbers like 1, 2, 3
    const aNum = parseInt(a.number);
    const bNum = parseInt(b.number);
    
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum;
    }
    
    // Handle alphanumeric sorting for letters like A, B, C or A01, A02
    return a.number.localeCompare(b.number, undefined, { numeric: true });
  });

  const leftSideCapacity = 20;
  const leftSide = sortedContainers.slice(0, leftSideCapacity);
  const rightSide = sortedContainers.slice(leftSideCapacity);

  const toggleContainerSelection = (container) => {
    setSelectedContainers(prev => (prev[0] === container.number ? [] : [container.number]));
  };

  const handleAddCustomerInput = (field, value) => {
    setAddCustomerForm(prev => ({ ...prev, [field]: value }));
    setAddCustomerError('');
  };

  // Initialize Stripe elements when modal opens / method changes
  useEffect(() => {
    const initStripe = async () => {
      try {
        if (!showAddCustomerModal) return;
        if (!window.Stripe) return;
        if (!process.env.REACT_APP_STRIPE_PUBLIC_KEY) return;
        // Initialize Stripe/elements once
        if (!stripeRef.current) {
          const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
          const elements = stripe.elements();
          stripeRef.current = stripe;
          elementsRef.current = elements;
        }
        // Destroy any existing inputs before switching
        try { cardRef.current && cardRef.current.destroy(); } catch {}
        try { bankRef.current && bankRef.current.destroy(); } catch {}
        cardRef.current = null;
        bankRef.current = null;

        // Create the chosen element
        if (paymentMethodType === 'card') {
          const card = elementsRef.current.create('card', { hidePostalCode: true });
          const mountNode = cardMountRef.current;
          if (mountNode) card.mount(mountNode);
          cardRef.current = card;
        } else if (paymentMethodType === 'au_becs_debit') {
          const bank = elementsRef.current.create('auBankAccount');
          const mountNode = bankMountRef.current;
          if (mountNode) bank.mount(mountNode);
          bankRef.current = bank;
        }
        setStripeReady(true);
      } catch (_) {
        setStripeReady(false);
      }
    };
    initStripe();
    return () => {
      try { cardRef.current && cardRef.current.destroy(); } catch {}
      try { bankRef.current && bankRef.current.destroy(); } catch {}
      stripeRef.current = null;
      elementsRef.current = null;
      cardRef.current = null;
      bankRef.current = null;
      setStripeReady(false);
    };
  }, [showAddCustomerModal, paymentMethodType]);

  const submitAddCustomer = async (e) => {
    e?.preventDefault?.();
    const containerNumber = selectedContainers[0];
    if (!containerNumber) return;
    if (!canAddCustomer) {
      setAddCustomerError('You can only add a customer to an Available container');
      return;
    }
    if (!addCustomerForm.firstName || !addCustomerForm.lastName || !addCustomerForm.email || !addCustomerForm.phone || !addCustomerForm.nextBillingDate) {
      setAddCustomerError('Please fill in all required fields');
      return;
    }

    try {
      setAddCustomerLoading(true);
      setAddCustomerError('');
      // 1) Init via the same endpoint to get SetupIntent (respect payment method type)
      const initResp = await fetch(`${process.env.REACT_APP_API_URL}/provision-existing-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          init: true,
          paymentMethodType,
          profile: {
            firstName: addCustomerForm.firstName,
            lastName: addCustomerForm.lastName,
            email: addCustomerForm.email,
            phone: addCustomerForm.phone
          }
        })
      });
      if (!initResp.ok) {
        const txt = await initResp.text().catch(() => '');
        throw new Error(`Setup init failed: ${initResp.status} ${txt}`);
      }
      const initData = await initResp.json();
      const clientSecret = initData.clientSecret;
      const stripeCustomerId = initData.customerId;
      if (!clientSecret || !stripeCustomerId) {
        throw new Error('Missing setup intent or customer');
      }

      if (!stripeRef.current) throw new Error('Stripe not ready');
      let result;
      if (paymentMethodType === 'card') {
        if (!cardRef.current) throw new Error('Card input not ready');
        result = await stripeRef.current.confirmCardSetup(clientSecret, {
          payment_method: { card: cardRef.current }
        });
      } else {
        if (!bankRef.current) throw new Error('Bank account input not ready');
        const fullName = `${addCustomerForm.firstName} ${addCustomerForm.lastName}`.trim();
        result = await stripeRef.current.confirmAuBecsDebitSetup(clientSecret, {
          payment_method: {
            au_becs_debit: bankRef.current,
            billing_details: {
              name: fullName,
              email: addCustomerForm.email
            }
          },
          mandate_data: {
            customer_acceptance: {
              type: 'online',
              online: { user_agent: navigator.userAgent }
            }
          }
        });
      }
      if (result.error) {
        throw new Error(result.error.message || 'Card setup failed');
      }
      const paymentMethodId = result.setupIntent?.payment_method;

      // 2) Provision customer, link container, set default payment method, send invite
      const response = await fetch(`${process.env.REACT_APP_API_URL}/provision-existing-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: site.id,
          containerNumber,
          stripeCustomerId,
          paymentMethodId,
          profile: {
            firstName: addCustomerForm.firstName,
            lastName: addCustomerForm.lastName,
            email: addCustomerForm.email,
            phone: addCustomerForm.phone,
            billingFrequency: addCustomerForm.billingFrequency,
            nextBillingDate: addCustomerForm.nextBillingDate
          },
          paymentMethodType,
          mandateAcceptedAt: new Date().toISOString()
        })
      });
      if (!response.ok) {
        const txt = await response.text().catch(() => '');
        throw new Error(`Failed to add customer: ${response.status} ${txt}`);
      }

      await loadContainers();
      setShowAddCustomerModal(false);
      setAddCustomerForm({ firstName: '', lastName: '', email: '', phone: '', billingFrequency: 'weekly', nextBillingDate: '' });
      setSelectedContainers([]);
      alert('Customer created, card saved, and assigned. Email sent.');
    } catch (err) {
      console.error('Add customer error:', err);
      setAddCustomerError(err.message || 'Failed to add customer');
    } finally {
      setAddCustomerLoading(false);
    }
  };

  const handleAddContainer = async () => {
    const raw = (bulkNumbersText || '').toUpperCase();
    const parts = raw
      .split(/\r?\n|,|;|\s+/)
      .map(s => s.trim())
      .filter(Boolean);

    // Deduplicate while preserving order
    const seen = new Set();
    const numbers = parts.filter(n => {
      if (seen.has(n)) return false;
      seen.add(n);
      return true;
    });

    if (numbers.length === 0) return;

    try {
      await Promise.all(numbers.map(n => createContainer(site.id, { number: n })));
      await loadContainers();
      setBulkNumbersText('');
      setShowAddModal(false);
    } catch (error) {
      console.error('Error creating containers:', error);
      alert('Error creating containers: ' + (error.message || 'Unknown error'));
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    try {
      const containerNumber = selectedContainers[0];
      if (!containerNumber) return;
      const updateData = { status: newStatus };
      if (newStatus === 'abandoned') {
        await updateContainer(site.id, containerNumber, updateData);
      } else if (newStatus === 'available') {
        // Full reset to brand new state (same ID)
        await updateContainerPayment(site.id, containerNumber, {
          status: 'available',
          subscriptionStatus: 'inactive',
          securityBondStatus: 'unpaid',
          billingFrequency: null,
          customerEmail: null,
          stripeCustomerId: null,
          stripeSubscriptionId: null,
          rentStartDate: null,
          lastPaymentDate: null,
          nextDueDate: null,
          overdueSince: null
        });
      } else if (newStatus === 'rented-paid') {
        await updateContainerPayment(site.id, containerNumber, {
          status: 'rented-paid',
          subscriptionStatus: 'active',
          securityBondStatus: 'paid'
        });
      } else if (newStatus === 'rented-unpaid') {
        await updateContainerPayment(site.id, containerNumber, {
          status: 'rented-unpaid',
          subscriptionStatus: 'past_due',
          securityBondStatus: 'paid'
        });
      }
      await loadContainers();
      setSelectedContainers([]);
      setShowBulkActions(false);
    } catch (error) {
      console.error('Error updating containers:', error);
      alert('Error updating containers');
    }
  };

  const openConfirm = (type) => {
    if (!selectedContainers[0]) return;
    setConfirmModal({ open: true, type });
  };

  const closeConfirm = () => setConfirmModal({ open: false, type: null });

  const confirmAction = async () => {
    if (!confirmModal.type) return;
    const type = confirmModal.type;
    closeConfirm();
    if (type === 'available') {
      await handleBulkStatusChange('available');
    } else if (type === 'abandoned') {
      await handleBulkStatusChange('abandoned');
    }
  };

  const handleSiteMapFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSiteMapFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setSiteMapPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please select a valid image file');
    }
  };

  const handleSiteMapDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setSiteMapFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setSiteMapPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please drop a valid image file');
    }
  };

  const handleSiteMapDragOver = (event) => {
    event.preventDefault();
  };

  const uploadSiteMapToS3 = async () => {
    if (!siteMapFile) return;

    try {
      setUploadingSiteMap(true);
      // Authorization header (if API is secured by Cognito authorizer)
      const getAuthHeader = async () => {
        try {
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession();
          const token = session?.tokens?.idToken?.toString() || session?.tokens?.accessToken?.toString();
          return token ? { Authorization: `Bearer ${token}` } : {};
        } catch (_) { return {}; }
      };
      
      // Convert file to base64 for API upload (following the same pattern as signature upload)
      const base64Data = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(siteMapFile);
      });

      // Upload via API to Lambda which replaces existing file in folder
      const fileName = siteMapFile.name;
      const slug = site.name.toLowerCase().replace(/\s+/g, '-');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/upload-sitemap`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(await getAuthHeader())
        },
        body: JSON.stringify({
          fileData: base64Data,
          fileName: fileName,
          siteInfo: {
            siteId: site.id,
            siteName: site.name
          }
        })
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }
      
      const result = await response.json();
      // Reload list to get signed URL of the newly uploaded file
      await loadExistingSiteMap();
      
      alert('Site map uploaded successfully!');
      
      // Clear the form
      setSiteMapFile(null);
      setSiteMapPreview(null);
      
      // Reload existing site map after short delay
      setTimeout(() => {
        loadExistingSiteMap();
      }, 1000);
      
    } catch (error) {
      console.error('Error uploading site map:', error);
      alert('Error uploading site map: ' + error.message);
    } finally {
      setUploadingSiteMap(false);
    }
  };

  const clearSiteMap = () => {
    setSiteMapFile(null);
    setSiteMapPreview(null);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading containers...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">{site.name}</h1>
            {site.address && (
              <p className="text-gray-600">{site.address}</p>
            )}
          </div>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Plus size={20} />
          Add Container
        </button>
      </div>

      {/* Container Grid - Original Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Side */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Left Side (1-{leftSideCapacity})</h2>
          {leftSide.length === 0 ? (
            <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
              No containers on left side
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {leftSide.map((container) => {
                const config = statusConfig[container.status] || statusConfig.available;
                const isSelected = selectedContainers.includes(container.number);
                return (
                  <div
                    key={container.number}
                    onClick={() => toggleContainerSelection(container)}
                    className={`
                      relative h-8 rounded-lg flex items-center justify-center text-white font-semibold text-sm cursor-pointer border-2 transition-all
                      ${config.color} ${config.textColor}
                      ${isSelected ? 'border-blue-500 scale-105' : 'border-transparent hover:scale-105'}
                    `}
                  >
                    {container.number}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Side */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Right Side ({leftSideCapacity + 1}+)</h2>
          {rightSide.length === 0 ? (
            <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
              No containers on right side
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {rightSide.map((container) => {
                const config = statusConfig[container.status] || statusConfig.available;
                const isSelected = selectedContainers.includes(container.number);
                return (
                  <div
                    key={container.number}
                    onClick={() => toggleContainerSelection(container)}
                    className={`
                      relative h-8 rounded-lg flex items-center justify-center text-white font-semibold text-sm cursor-pointer border-2 transition-all
                      ${config.color} ${config.textColor}
                      ${isSelected ? 'border-blue-500 scale-105' : 'border-transparent hover:scale-105'}
                    `}
                  >
                    {container.number}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Legend below container grid */}
      <div className="mt-4 mb-2">
        <div className="flex items-center gap-6 text-sm text-gray-700">
          <div className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded-sm bg-gray-400"></span>
            <span>Available</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded-sm bg-green-500"></span>
            <span>Rented</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded-sm bg-yellow-500"></span>
            <span>Rented - Unpaid</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded-sm bg-red-500"></span>
            <span>Abandoned</span>
          </div>
        </div>
      </div>

      {/* Action bar below grid */}
      {showBulkActions && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              className={`px-3 py-1 rounded text-sm font-medium border ${canViewCustomer ? 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100' : 'bg-gray-300 text-gray-500 border-gray-300 cursor-not-allowed'}`}
              onClick={() => {
                if (!canViewCustomer) return;
                const email = selectedContainerObj.customerEmail;
                navigate(`/admin/clients?search=${encodeURIComponent(email)}`);
              }}
              type="button"
            >
              Customer
            </button>
            <button
              className={`px-3 py-1 rounded text-sm font-medium border ${canAddCustomer ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' : 'bg-gray-300 text-gray-500 border-gray-300 cursor-not-allowed'}`}
              onClick={() => { if (canAddCustomer) setShowAddCustomerModal(true); }}
              type="button"
            >
              Add Customer
            </button>
            <button
              onClick={() => openConfirm('available')}
              className={`px-3 py-1 rounded text-sm font-medium ${statusConfig.available.color} ${statusConfig.available.textColor} hover:opacity-80`}
            >
              Mark as Available
            </button>
            <button
              onClick={() => openConfirm('abandoned')}
              className={`px-3 py-1 rounded text-sm font-medium ${statusConfig.abandoned.color} ${statusConfig.abandoned.textColor} hover:opacity-80`}
            >
              Mark as Abandoned
            </button>
            <button
              onClick={() => setSelectedContainers([])}
              className="px-3 py-1 rounded text-sm font-medium bg-gray-300 text-gray-700 hover:bg-gray-400"
            >
              Clear Selection
            </button>
            <button
              onClick={() => setDeleteContainerModal(true)}
              className="px-3 py-1 rounded text-sm font-medium bg-red-600 text-white hover:bg-red-700"
            >
              Delete Container
            </button>
          </div>
        </div>
      )}

      {/* Confirm Modals */}
      {confirmModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-2">
              {confirmModal.type === 'available' ? 'Mark as Available' : 'Mark as Abandoned'}
            </h2>
            <p className="text-sm text-gray-700 mb-4">
              {confirmModal.type === 'available'
                ? 'This will reset the container to a brand new state (same ID). All customer and payment data will be cleared.'
                : 'This will mark the container as abandoned and keep all existing data.'}
            </p>
            <div className="bg-gray-50 rounded p-3 text-sm text-gray-800 mb-4">
              Container: {selectedContainers[0]}
            </div>
            <div className="flex gap-3">
              <button
                onClick={confirmAction}
                className={`flex-1 ${confirmModal.type === 'available' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-red-600 hover:bg-red-700'} text-white py-2 rounded-lg`}
              >
                Confirm
              </button>
              <button
                onClick={closeConfirm}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Site Map Section */}
      <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <MapPin size={24} />
          Site Map Management
        </h2>
        
        <div className="space-y-4">
          {/* Show existing site map if available */}
          {siteMapUrl && !siteMapPreview && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-800">Current Site Map:</span>
                <button
                  onClick={() => setSiteMapUrl(null)}
                  className="text-blue-600 hover:text-blue-800 text-sm underline"
                >
                  Replace Map
                </button>
              </div>
              <div className="mb-3">
                <img
                  src={siteMapUrl}
                  alt="Current site map"
                  className="max-w-full h-auto max-h-48 rounded border object-contain"
                />
              </div>
              <button
                onClick={() => window.open(siteMapUrl, '_blank')}
                className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
              >
                <Image size={16} />
                View Full Size
              </button>
            </div>
          )}
          
          {!siteMapPreview && !siteMapUrl ? (
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-orange-400 transition-colors cursor-pointer"
              onDrop={handleSiteMapDrop}
              onDragOver={handleSiteMapDragOver}
              onClick={() => document.getElementById('siteMapInput').click()}
            >
              <Upload size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-600 mb-2">Upload Site Map</p>
              <p className="text-sm text-gray-500 mb-4">
                Drag and drop an image file here, or click to select
              </p>
              <input
                id="siteMapInput"
                type="file"
                accept="image/*"
                onChange={handleSiteMapFileSelect}
                className="hidden"
              />
              <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                <Image size={16} />
                Supported: JPG, PNG, GIF, WebP
              </div>
            </div>
          ) : siteMapPreview ? (
            <div className="space-y-4">
              <div className="relative bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Preview:</span>
                  <button
                    onClick={clearSiteMap}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={20} />
                  </button>
                </div>
                <img
                  src={siteMapPreview}
                  alt="Site map preview"
                  className="max-w-full h-auto max-h-64 rounded border"
                />
                <p className="text-sm text-gray-600 mt-2">{siteMapFile?.name}</p>
              </div>
              
              <button
                onClick={uploadSiteMapToS3}
                disabled={uploadingSiteMap}
                className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg flex items-center gap-2"
              >
                <Upload size={20} />
                {uploadingSiteMap ? 'Uploading...' : 'Save Site Map'}
              </button>
            </div>
          ) : null}
          
          {siteMapUrl && siteMapPreview && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-sm">
                ✓ Site map uploaded successfully and saved to cloud storage
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Add Container Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add Containers</h2>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">Container Numbers</label>
              <textarea
                rows={6}
                placeholder={'Enter one per line or comma-separated\nExamples: A1, A2, A3 or 1, 2, 3'}
                value={bulkNumbersText}
                onChange={(e) => setBulkNumbersText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <p className="text-xs text-gray-500">Duplicates and blanks are ignored. Letters/numbers are uppercased.</p>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleAddContainer}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg"
              >
                Add Containers
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteContainerModal && selectedContainers[0] && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-3 text-red-700">Delete Container</h2>
            <p className="text-sm text-gray-700 mb-4">
              Are you sure you want to delete container <span className="font-semibold">{selectedContainers[0]}</span> from this site? This will permanently remove it from DynamoDB. This cannot be undone.
            </p>
            <label className="inline-flex items-center gap-2 text-sm text-gray-800 mb-4">
              <input
                type="checkbox"
                checked={deleteContainerChecked}
                onChange={(e) => setDeleteContainerChecked(e.target.checked)}
              />
              I understand this action cannot be undone
            </label>
            <div className="flex gap-3">
              <button
                onClick={async () => {
                  if (!deleteContainerChecked) return;
                  try {
                    setDeleteContainerLoading(true);
                    await deleteContainer(site.id, selectedContainers[0]);
                    await loadContainers();
                    setSelectedContainers([]);
                    setDeleteContainerModal(false);
                    setDeleteContainerChecked(false);
                  } catch (err) {
                    console.error('Error deleting container:', err);
                    alert('Error deleting container');
                  } finally {
                    setDeleteContainerLoading(false);
                  }
                }}
                disabled={!deleteContainerChecked || deleteContainerLoading}
                className={`flex-1 ${deleteContainerChecked && !deleteContainerLoading ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-300'} text-white py-2 rounded-lg`}
              >
                {deleteContainerLoading ? 'Deleting…' : 'Delete Container'}
              </button>
              <button
                onClick={() => { setDeleteContainerModal(false); setDeleteContainerChecked(false); }}
                disabled={deleteContainerLoading}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">Add Customer to Container {selectedContainers[0]}</h2>
            {addCustomerError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-3 text-sm">
                {addCustomerError}
              </div>
            )}
            <form onSubmit={submitAddCustomer} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={addCustomerForm.firstName}
                    onChange={(e) => handleAddCustomerInput('firstName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={addCustomerForm.lastName}
                    onChange={(e) => handleAddCustomerInput('lastName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={addCustomerForm.email}
                    onChange={(e) => handleAddCustomerInput('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input
                    type="tel"
                    value={addCustomerForm.phone}
                    onChange={(e) => handleAddCustomerInput('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method *</label>
                <div className="flex gap-3 mb-3">
                  <label className={`px-3 py-2 border rounded-lg cursor-pointer ${paymentMethodType === 'card' ? 'border-orange-500 bg-orange-50' : 'border-gray-300'}`}>
                    <input type="radio" name="pmtype" value="card" className="sr-only" checked={paymentMethodType === 'card'} onChange={() => setPaymentMethodType('card')} />
                    Card
                  </label>
                  <label className={`px-3 py-2 border rounded-lg cursor-pointer ${paymentMethodType === 'au_becs_debit' ? 'border-orange-500 bg-orange-50' : 'border-gray-300'}`}>
                    <input type="radio" name="pmtype" value="au_becs_debit" className="sr-only" checked={paymentMethodType === 'au_becs_debit'} onChange={() => setPaymentMethodType('au_becs_debit')} />
                    Bank account (AU)
                  </label>
                </div>
                {paymentMethodType === 'card' ? (
                  <div ref={cardMountRef} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                ) : (
                  <div>
                    <div ref={bankMountRef} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    <p className="text-xs text-gray-600 mt-2">Test BECS details: BSB 000-000, Account 000123456</p>
                  </div>
                )}
                {!process.env.REACT_APP_STRIPE_PUBLIC_KEY && (
                  <div className="text-xs text-red-600 mt-1">Missing REACT_APP_STRIPE_PUBLIC_KEY</div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Frequency *</label>
                  <select
                    value={addCustomerForm.billingFrequency}
                    onChange={(e) => handleAddCustomerInput('billingFrequency', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Next Billing Date *</label>
                  <input
                    type="date"
                    value={addCustomerForm.nextBillingDate}
                    onChange={(e) => handleAddCustomerInput('nextBillingDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={addCustomerLoading}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white py-2 rounded-lg"
                >
                  {addCustomerLoading ? 'Adding...' : 'Add Customer'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddCustomerModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}



    </div>
  );
}

function Containers() {
  const [currentView, setCurrentView] = useState('sites');
  const [selectedSite, setSelectedSite] = useState(null);

  const handleSiteSelect = (site) => {
    setSelectedSite(site);
    setCurrentView('containers');
  };

  const handleBackToSites = () => {
    setCurrentView('sites');
    setSelectedSite(null);
  };

  return (
    <>
      {currentView === 'sites' && (
        <SiteLocations onSiteSelect={handleSiteSelect} />
      )}
      {currentView === 'containers' && selectedSite && (
        <ContainerGrid site={selectedSite} onBack={handleBackToSites} />
      )}
    </>
  );
}

export default Containers;
